import { useCallback, useEffect, useReducer, useRef } from "react";
import { apiClient, useApiQuery } from "../../../services/api";
import { messageText, type ConversationMessage } from "../transcript";
import {
  GPT_ACTIVE_TURN_STALE_MS,
  liveActiveTurn,
} from "../../../../../api/src/shared/domain/gpt-turn";
import type { GptStreamFailure } from "../../../../../api/src/shared/gpt-stream-failure";

// A DROPPED CONNECTION IS NOT A LOST ANSWER.
//
// api's `streamGeneralChatTurn` runs the LLM on its OWN AbortController — never
// the request's — and calls `result.consumeStream()`, so the agent finishes the
// turn and writes it to the transcript whether or not the browser is still
// listening. iOS suspending the webview when the app is backgrounded, a tunnel, a
// Wi-Fi/5G handover: each of those kills the SSE connection and none of them
// kills the answer. Reloading the page showed the answer sitting in the
// conversation while the chat itself displayed "❌ Sorry an error has occurred.
// Please retry." — an error message over a question that had been answered.
//
// So a broken stream is not an error until proven one. It opens a RECOVERY WINDOW
// instead: ask the server for the persisted turn until it lands, then adopt it as
// history — exactly what that reload showed. Only a turn that never lands inside
// the window gets the error surface.
//
// Why polling rather than `chat.resumeStream()`: resuming the SSE itself requires
// the stream to be replayable server-side (a resumable-stream store, i.e. Redis
// pub/sub). We have no such store and need none, because the DB already holds the
// finished turn. The window is the only thing invented here.

// How often we ask, and how many times before calling the turn lost. Counted in
// ANSWERED probes, never in wall-clock: iOS freezes timers while the app is
// backgrounded, so a `Date.now()` deadline would already have expired by the time
// the user is looking at the screen again — declaring failure at the exact moment
// the answer was there for the taking.
//
// The ceiling is DERIVED from the marker's own lifetime. A probe keeps the window
// open only while the conversation carries a fresh in-flight marker (see
// `ProbeOutcome`), and a marker is fresh for `GPT_ACTIVE_TURN_STALE_MS` at most:
// by the time the ceiling is reached no marker can still be fresh, so the turn
// it described has either landed or died with its instance. A shorter ceiling
// would call a turn lost, stop the chat and offer Retry while the server may
// still be writing it — and that Retry would be refused with 409.
const PROBE_INTERVAL_MS = 3_000;
const MAX_PROBES = Math.ceil(GPT_ACTIVE_TURN_STALE_MS / PROBE_INTERVAL_MS);

// How many CONSECUTIVE probes may find the server reachable, not writing the
// turn, and not holding its answer before we stop waiting. This is the fast exit
// the ceiling alone never gave: a request that never reached the server (the
// webview was suspended before the socket opened), or a turn the server failed
// before persisting, used to sit on "reconnecting" for the whole window.
//
// Not one probe: the server writes the marker as soon as it takes the turn
// (right after the per-conversation lock, before any of the setup work), but a
// live disconnect can race that write by a round-trip, and a phone's network can
// stretch a round-trip. ~24 seconds is past both on any day the server is
// healthy.
const IDLE_PROBES_BEFORE_GIVING_UP = 8;

// While a probe loop runs UNDER a live stream (see `watching`), a landing is held
// for one interval before it is adopted. The server persists the turn as the
// last frames leave for the client, so a healthy stream that is merely finishing
// would otherwise be cut off and replaced by the persisted copy — dropping the
// live-only parts (the proposal card) the persisted transcript does not carry.
// If the stream ends inside the grace, its own hand-off settles the turn and the
// held landing is dropped by the cancellation token.
const LANDING_GRACE_MS = PROBE_INTERVAL_MS;

// Enough to hold the conversation a pending FIRST turn belongs to: it is the
// newest one, and the list is ordered newest-first.
const PROBE_LIST_LIMIT = 5;

/**
 * What one probe learned. Three answers, because "not yet" hides two situations
 * that call for opposite behaviour:
 *
 * - `working`: the server is still writing the turn (its in-flight marker is set
 *   and fresh), OR the probe could not reach the server at all — in both cases
 *   waiting is the right thing, however long it takes.
 * - `idle`: the server answered, holds no in-flight marker for this turn, and the
 *   transcript does not contain it. Nothing is being written: either the request
 *   never arrived, or the turn failed before it was persisted. Waiting can only
 *   delay the Retry the user needs.
 */
export type ProbeOutcome<Landed> =
  { kind: "landed"; landed: Landed } | { kind: "working" } | { kind: "idle" };

/**
 * Where a turn stands with respect to its connection. `Landmark` is how the
 * surface recognises its own turn in what the server has written (see the surface
 * hooks) — captured when the turn is handed over, from the LIVE chat rather than
 * from a query cache, which can still be a turn behind.
 *
 * `extends object` so a landmark can never itself be null: that keeps
 * `Landmark | null` narrowable, which an unconstrained parameter is not.
 */
type RecoveryState<Landmark extends object> =
  // No turn in flight, or the last one is done — streamed, recovered or given up
  // on by the user.
  | { kind: "settled" }
  // Handed to the server, connection nominally alive. `watching` is set once the
  // app has been to the background and back with this turn in flight: a socket
  // the OS suspended does not always report its death — it can simply never
  // deliver another byte — so from that moment the persisted turn is ALSO polled
  // for, silently, under the stream. Whichever lands first wins; nothing about
  // the surface changes (the turn still reads as streaming, not reconnecting).
  | { kind: "sent"; landmark: Landmark; watching: boolean }
  // The connection broke; the server is presumed to still be writing the answer.
  | { kind: "recovering"; landmark: Landmark }
  // The server refused outright, or the window closed and the turn never landed.
  // This one really is an error, and the error surface takes over. Carries the
  // turn given up on, so the surface can retry it as what it was; null for a
  // refusal of a turn that was never tracked.
  | { kind: "lost"; landmark: Landmark | null };

type RecoveryEvent<Landmark extends object> =
  // A question (or a replay) went to the server. A null landmark means this
  // surface cannot recognise the turn it is sending (see the surface hooks), so
  // there is nothing to come back to and the turn is not tracked.
  | { type: "sent"; landmark: Landmark | null }
  // Streamed to the end, or the user walked away from it.
  | { type: "settled" }
  | { type: "failed"; failure: GptStreamFailure }
  // The persisted turn was found and handed to the surface.
  | { type: "landed" }
  // A turn this surface did NOT stream is already in flight (recovered on a cold
  // mount from the conversation's `activeTurnStartedAt`): go straight to
  // recovering — there is no live stream here to break, the server is presumed
  // still writing.
  | { type: "recover"; landmark: Landmark }
  // The app came back to the foreground with a turn in flight: start polling
  // under the live stream (see `watching`).
  | { type: "watch" }
  // The window closed with nothing to show.
  | { type: "exhausted" };

/**
 * A REDUCER, not `useState` — and that is load-bearing rather than taste. The
 * question is handed over and the transport can fail in the same tick, before
 * React has committed the "sent" render: `setState` handlers reading state from
 * their render closure would still see "nothing in flight" and drop the recovery
 * on the floor (which is precisely what the test for this pins down). Reducer
 * events are queued and applied in order against the real latest state.
 */
function reduceRecovery<Landmark extends object>(
  state: RecoveryState<Landmark>,
  event: RecoveryEvent<Landmark>
): RecoveryState<Landmark> {
  switch (event.type) {
    case "sent":
      return event.landmark === null
        ? { kind: "settled" }
        : { kind: "sent", landmark: event.landmark, watching: false };
    case "settled":
    case "landed":
      return { kind: "settled" };
    case "failed":
      // A status means the server ANSWERED: out of credits, a turn already in
      // progress, a malformed request, a fault before the stream. No turn is
      // running, so waiting could only delay the message the user needs — the
      // out-of-credits notice most of all.
      // The same for a failure the server reported inside the stream: it
      // persists nothing then, so a probe could only find nothing, slowly.
      if (endsTheTurn(event.failure))
        return { kind: "lost", landmark: trackedLandmark(state) };
      // A break on a turn we are not tracking stays exactly as untracked as it
      // was: with no landmark there is no way to tell a recovered turn from the
      // transcript as it already stood.
      //
      // The SAME landmark object carries over, on purpose: the probe loop is
      // keyed on it, so a watch already running under the stream continues
      // uninterrupted (its counters included) rather than starting over.
      return state.kind === "sent"
        ? { kind: "recovering", landmark: state.landmark }
        : state;
    case "recover":
      return { kind: "recovering", landmark: event.landmark };
    case "watch":
      return state.kind === "sent" ? { ...state, watching: true } : state;
    case "exhausted":
      return { kind: "lost", landmark: trackedLandmark(state) };
    default:
      throw new Error(event satisfies never);
  }
}

// A refusal, or a failure the server reported in the stream: no turn is running
// any more. A disconnect leaves the server writing it.
function endsTheTurn(failure: GptStreamFailure): boolean {
  switch (failure.kind) {
    case "rejected":
    case "failed":
      return true;
    case "disconnected":
      return false;
    default:
      throw new Error(failure satisfies never);
  }
}

function trackedLandmark<Landmark extends object>(
  state: RecoveryState<Landmark>
): Landmark | null {
  switch (state.kind) {
    case "sent":
    case "recovering":
    case "lost":
      return state.landmark;
    case "settled":
      return null;
    default:
      throw new Error(state satisfies never);
  }
}

/** The part of a recovery every surface drives and reads the same way. */
type RecoveryControls = {
  // True while waiting out a broken connection. The surface must keep presenting
  // the turn as in flight and must NOT offer Retry: the server is still working
  // on it and answers a re-ask with 409.
  isReconnecting: boolean;
  // True once the tracked turn has been given up on: the server refused it, or
  // the window closed without it landing. The surface shows its failure surface
  // on this — the chat's own status cannot say so for a turn the surface never
  // streamed itself (a cold mount) or for a stream that died in silence.
  isLost: boolean;
  // The turn is over as far as this surface is concerned — it streamed to the
  // end, or the user abandoned it (stop button, leaving the page). Stop asking.
  onTurnSettled: () => void;
  // The transport gave up on the turn. Classified at the fetch boundary, not
  // sniffed out of a message (see `GptStreamFailure`).
  onFailure: (failure: GptStreamFailure) => void;
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Ask `probe` until it hands back the recovered turn, or until the window closes.
 */
function useProbeLoop<Landmark extends object, Landed>(args: {
  // Non-null exactly while a turn is being polled for. Also the effect's key: it
  // goes back through null on the way to any later turn, so a second broken turn
  // gets a full window of its own even with an identical landmark.
  landmark: Landmark | null;
  // TOTAL: a request that fails is the connection still being down, which is a
  // state worth waiting out — the probe answers `working`, it does not throw.
  probe: (landmark: Landmark) => Promise<ProbeOutcome<Landed>>;
  // Takes the cancellation token the probe was started under, so a result that
  // arrives after the turn ended is dropped rather than landed.
  onLanded: (landed: Landed, probedTurn: number) => void;
  onLost: () => void;
  currentTurn: () => number;
  // Read at LANDING time (the loop can span the sent → recovering transition):
  // how long to hold a landing before adopting it — non-zero only while a live
  // stream may still be finishing on its own (see `LANDING_GRACE_MS`).
  landingGraceMs: () => number;
}): void {
  // Re-read on every tick rather than captured when the loop started, so the
  // probe and the callbacks see the current render's values.
  const latest = useRef(args);
  latest.current = args;
  const landmark = args.landmark;

  useEffect(() => {
    if (landmark === null) return;
    let isCancelled = false;
    const probedTurn = latest.current.currentTurn();

    // An arrow bound after the null check, not a hoisted declaration: TS keeps the
    // narrowing of a const only for closures created after it.
    const ask = async (): Promise<void> => {
      let idleStreak = 0;
      for (let asked = 0; asked < MAX_PROBES; asked += 1) {
        const outcome = await latest.current.probe(landmark);
        if (isCancelled) return;
        if (outcome.kind === "landed") {
          const grace = latest.current.landingGraceMs();
          if (grace > 0) {
            await sleep(grace);
            if (isCancelled) return;
          }
          latest.current.onLanded(outcome.landed, probedTurn);
          return;
        }
        // Only an unbroken run of `idle` answers means nobody is writing the
        // turn: a single `working` in between (the marker showed up, or the
        // network dropped) starts the count over.
        idleStreak = outcome.kind === "idle" ? idleStreak + 1 : 0;
        if (idleStreak >= IDLE_PROBES_BEFORE_GIVING_UP) break;
        await sleep(PROBE_INTERVAL_MS);
        if (isCancelled) return;
      }
      latest.current.onLost();
    };

    ask().catch(() => {
      // A probe broke its total contract. Whatever the bug is, the user must not
      // be left on a reconnecting row forever: close the window and let the
      // failure surface take over.
      if (!isCancelled) latest.current.onLost();
    });
    return () => {
      isCancelled = true;
    };
  }, [landmark]);
}

/**
 * The state machine both surfaces share. They differ only in what they recognise
 * their own persisted turn by (`Landmark`) and what they get back (`Landed`).
 */
function useRecoveryMachine<Landmark extends object, Landed>(args: {
  probe: (landmark: Landmark) => Promise<ProbeOutcome<Landed>>;
  onLanded: (landed: Landed) => void;
  // The window closed on the tracked turn. The surface stops whatever the chat
  // still believes it is doing: a stream that died in silence is still
  // "streaming" as far as the SDK knows, and would lock the composer forever.
  onLost: () => void;
}): RecoveryControls & {
  // Null landmark = this surface cannot recognise the turn it is about to send
  // (see the surface hooks), so a break on it stays a plain error rather than a
  // blind wait.
  begin: (landmark: Landmark | null) => void;
  // Adopt an already-in-flight turn straight into the recovering window (cold
  // mount, recovered from the conversation's server-side in-flight state).
  recover: (landmark: Landmark) => void;
  // The turn given up on while `isLost`, or null.
  lostTurn: Landmark | null;
} {
  const [state, dispatch] = useReducer(reduceRecovery<Landmark>, {
    kind: "settled",
  } satisfies RecoveryState<Landmark>);
  const latest = useRef({ args, state });
  latest.current = { args, state };

  // Cancellation token for probes already in flight, bumped SYNCHRONOUSLY by every
  // transition that ends the tracked turn. A probe that started before the user
  // pressed Stop (or before a later turn began) resolves afterwards, and landing it
  // then would navigate away from — or overwrite — a turn the surface has moved on
  // from. A rendered flag cannot guard that: the probe can resolve in the same tick
  // as the click that cancelled it, before React has committed anything.
  const turn = useRef(0);
  const endTurn = useCallback(() => {
    turn.current += 1;
  }, []);

  const land = useCallback(
    (landed: Landed, probedTurn: number) => {
      if (probedTurn !== turn.current) return;
      endTurn();
      latest.current.args.onLanded(landed);
      dispatch({ type: "landed" });
    },
    [endTurn]
  );

  // The loop runs in two states: openly while reconnecting, and silently under a
  // live stream the app has been suspended over (`watching`). The landmark object
  // is the same across that transition, so the loop is not restarted by it.
  const probedLandmark =
    state.kind === "recovering" || (state.kind === "sent" && state.watching)
      ? state.landmark
      : null;
  useProbeLoop<Landmark, Landed>({
    landmark: probedLandmark,
    probe: (landmark) => latest.current.args.probe(landmark),
    onLanded: land,
    onLost: () => {
      endTurn();
      latest.current.args.onLost();
      dispatch({ type: "exhausted" });
    },
    currentTurn: () => turn.current,
    // A live stream may still be finishing on its own only in the `sent` state.
    landingGraceMs: () =>
      latest.current.state.kind === "sent" ? LANDING_GRACE_MS : 0,
  });

  // Coming back to the foreground with a turn in flight. A webview the OS
  // suspended does not always report the break as an error — the socket can simply
  // never deliver another byte, which leaves the turn on "Thinking…" with nothing
  // to time out against. So from the moment the app comes back, poll for the
  // persisted turn UNDER the stream: if the server has already written it, or
  // writes it later, the stream is over whatever its status still claims, and the
  // persisted turn is the truth. A single probe on return is not enough — it only
  // catches an answer that had already landed by then, and a turn still being
  // written on a dead socket hung forever. (While recovering, the loop is already
  // running and the next ask is at most one interval away.)
  const isSentUnwatched = state.kind === "sent" && !state.watching;
  useEffect(() => {
    if (!isSentUnwatched) return;
    const watchOnReturn = (): void => {
      if (document.visibilityState !== "visible") return;
      dispatch({ type: "watch" });
    };
    document.addEventListener("visibilitychange", watchOnReturn);
    return () => {
      document.removeEventListener("visibilitychange", watchOnReturn);
    };
  }, [isSentUnwatched]);

  return {
    isReconnecting: state.kind === "recovering",
    isLost: state.kind === "lost",
    lostTurn: state.kind === "lost" ? state.landmark : null,
    begin: (landmark) => {
      // A new turn supersedes the previous one's probes, landmark or no landmark.
      endTurn();
      dispatch({ type: "sent", landmark });
    },
    // Adopt an already-in-flight turn (cold mount, recovered from the pending
    // store) straight into the recovering window — no live stream here to break.
    recover: (landmark: Landmark) => {
      endTurn();
      dispatch({ type: "recover", landmark });
    },
    onTurnSettled: () => {
      endTurn();
      dispatch({ type: "settled" });
    },
    onFailure: (failure) => {
      // Only a REFUSAL or a reported failure ends the turn here: a disconnect
      // keeps the same turn (and therefore the same token) alive on its way
      // into the recovery window.
      if (endsTheTurn(failure)) endTurn();
      dispatch({ type: "failed", failure });
    },
  };
}

/**
 * What the surface sends, described so it can be recognised in the transcript the
 * server writes: the question, plus the answer the conversation ENDED with at that
 * moment (null when it did not end with one).
 *
 * Both come from the live chat, and both are needed:
 *
 * - The question alone is not enough. The transcript already ends with that
 *   question whenever the turn is a `regenerate`, or simply the same question
 *   asked twice in a row — the pre-turn transcript would read as landed and the
 *   in-flight question would vanish off the screen.
 * - `updatedAt` (what this used to compare) is not enough either, in the other
 *   direction: ANY write to the row bumps it, so minting a share link mid-recovery
 *   completed the recovery against a transcript that had no new turn in it. It also
 *   had to be read from the query cache, which the previous turn only refreshes
 *   through an unawaited revalidate — a quick follow-up could therefore start out
 *   with a landmark one turn stale and instantly "recover" the previous turn.
 *
 * Reading both off the live chat instead settles all three: no cache, no clocks,
 * and nothing but the transcript's own content can satisfy it. The one thing it
 * cannot see is a replayed answer that comes back byte-identical — that turn waits
 * out the window and gets the error surface, with Retry.
 */
type SentTurn = {
  // A new question, or a Regenerate of the one the transcript ends with. Read
  // when the turn is lost: a failed Regenerate left the server's turn in place,
  // so it is retried as a Regenerate, never appended again.
  kind: "ask" | "regenerate";
  question: string;
  endedWith: string | null;
};

/**
 * Recovery on an EXISTING conversation (`/webapp/private/conversations/:id`).
 */
export function useConversationStreamRecovery(args: {
  conversationId: string;
  // The persisted transcript, the moment the server has the turn. The PAGE adopts
  // it (`chat.setMessages` + `clearError`) rather than this hook, so the hook owns
  // one question only — "has the turn landed" — and never touches the chat.
  onTurnRecovered: (messages: ConversationMessage[]) => void;
  // The window closed on the tracked turn (see `RecoveryControls.isLost`).
  onTurnLost: () => void;
}): RecoveryControls & {
  onTurnSent: (turn: SentTurn) => void;
  onTurnInFlight: (turn: SentTurn) => void;
  // The turn given up on while `isLost`, or null when none was tracked.
  lostTurn: SentTurn | null;
} {
  // The very query the page seeds its history from: same key, so this shares its
  // cache entry instead of adding a fetch. The long `staleTime` applies to THIS
  // observer only (it keeps it from re-fetching on mount); the probe's explicit
  // `refetch` ignores staleness entirely.
  const conversationQuery = useApiQuery(
    "/webapp/private/conversations/:id",
    args.conversationId === ""
      ? false
      : { param: { id: args.conversationId }, staleTime: 60_000 }
  );
  // Null on the idle query (no conversation id), which is also the one case with
  // nothing to probe.
  const refetch =
    conversationQuery.state === "idle" ? null : conversationQuery.refetch;
  const onTurnRecovered = args.onTurnRecovered;
  const onTurnLost = args.onTurnLost;

  const machine = useRecoveryMachine<SentTurn, ConversationMessage[]>({
    probe: async (sent) => {
      if (refetch === null) return { kind: "working" };
      const result = await refetch();
      // A failed refetch is the connection still being down — and it leaves the
      // PREVIOUS response in `data` (react-query never drops cached data on
      // error), which must not be read as the server's current word on the turn.
      if (result.error !== null) return { kind: "working" };
      const output = result.data;
      if (output === undefined) return { kind: "working" };
      // Returned from the probe rather than read off the render: the loop calls
      // back before React has re-rendered with the refetched cache, and reading
      // the component's copy there would adopt the PRE-turn transcript — dropping
      // the user's question from the screen.
      if (hasLanded(output.body.messages, sent))
        return { kind: "landed", landed: output.body.messages };
      // The server's in-flight marker is the word on whether anyone is still
      // writing this turn. Compared on the question: a marker for a DIFFERENT
      // question is another turn's, and ours cannot be running behind it (the
      // per-conversation lock would have refused ours with 409).
      const active = liveActiveTurn(output.body, Date.now());
      return active !== null && active.question.trim() === sent.question.trim()
        ? { kind: "working" }
        : { kind: "idle" };
    },
    onLanded: onTurnRecovered,
    onLost: onTurnLost,
  });

  return {
    isReconnecting: machine.isReconnecting,
    isLost: machine.isLost,
    lostTurn: machine.lostTurn,
    onTurnSettled: machine.onTurnSettled,
    onFailure: machine.onFailure,
    onTurnSent: (turn) => {
      machine.begin(turn.question.trim() === "" ? null : turn);
    },
    // A turn was already in flight when this page mounted (recovered from the
    // conversation's server-side `activeTurnStartedAt`): go straight to the
    // recovering window — there is no live stream here to break — so the surface
    // shows it reconnecting and the probe loop polls for the persisted answer.
    onTurnInFlight: (turn: SentTurn) => {
      if (turn.question.trim() === "") return;
      machine.recover(turn);
    },
  };
}

/**
 * The turn a persisted transcript ENDS with — question and answer — or null when it
 * does not end with an answered turn (an onboarding opener, or an empty
 * conversation). Structural over both transcript shapes the endpoints return: the
 * detail's hydrated sources and the list's raw references both carry `role` +
 * `parts`, and `messageText` only reads the text parts.
 */
function lastTurnOf(
  messages: ReadonlyArray<{
    role: "user" | "assistant";
    parts: Parameters<typeof messageText>[0]["parts"];
  }>
): { question: string; answer: string } | null {
  const last = messages.at(-1);
  if (last === undefined || last.role !== "assistant") return null;
  const answer = messageText(last);
  for (let index = messages.length - 2; index >= 0; index -= 1) {
    const message = messages[index];
    if (message === undefined) continue;
    if (message.role === "user")
      return { question: messageText(message), answer };
  }
  return null;
}

/**
 * Does this persisted transcript already contain the turn that was sent? Exported
 * for its own test — it is the whole correctness argument of the conversation
 * recovery (see `SentTurn`).
 */
export function hasLanded(
  messages: Parameters<typeof lastTurnOf>[0],
  sent: Pick<SentTurn, "question" | "endedWith">
): boolean {
  const turn = lastTurnOf(messages);
  if (turn === null) return false;
  if (turn.question.trim() !== sent.question.trim()) return false;
  // The transcript ends with this question AND with the answer it already ended
  // with, so nothing has been added: a replay still running, the same question
  // asked twice, or an unrelated write to the row.
  return turn.answer !== sent.endedWith;
}

/**
 * Recovery on the new-chat landing, where the conversation row is only written
 * once the turn completes. Landing therefore IS the conversation showing up in
 * the history list — which hides empty transcripts, so the same probe covers the
 * attachment flow (a row created up front to hold the file, then appended to).
 *
 * The list and not `GET /conversations/:id`: for most of the window that row does
 * not exist, and forty 404s would be forty Sentry issues.
 *
 * This surface has no in-flight marker to read (the row it would sit on does not
 * exist yet), so a probe that finds nothing answers `working` and the window is
 * only ever closed by its ceiling.
 */
export function useNewConversationStreamRecovery(args: {
  // Read at PROBE time, not at render: the id rides the stream itself
  // (`data-conversation`), so when the question is sent there is none yet.
  getConversationId: () => string | null;
  onTurnRecovered: (conversationId: string) => void;
  // The window closed on the tracked turn (see `RecoveryControls.isLost`).
  onTurnLost: () => void;
}): RecoveryControls & { onTurnSent: (question: string) => void } {
  const getConversationId = args.getConversationId;
  const onTurnRecovered = args.onTurnRecovered;
  const onTurnLost = args.onTurnLost;

  // The landmark is the QUESTION, because it doubles as the fallback identity: the
  // create route only starts streaming once its retrieval prefetch is done, so a
  // webview suspended in those first seconds never receives the id, and the
  // conversation then has to be recognised by what was asked (see
  // `matchPendingConversation`).
  const machine = useRecoveryMachine<{ question: string }, string>({
    probe: async ({ question }) => {
      try {
        const { body } = await apiClient.get("/webapp/private/conversations", {
          query: { limit: PROBE_LIST_LIMIT, offset: 0, sort: "newest" },
        });
        const landed = matchPendingConversation(
          body.docs,
          getConversationId(),
          question
        );
        return landed === null
          ? { kind: "working" }
          : { kind: "landed", landed };
      } catch {
        // No user feedback and nothing captured: a probe that cannot reach the
        // server is the connection still being down, which is the exact state the
        // reconnecting row on screen is already reporting. Answer "not yet".
        return { kind: "working" };
      }
    },
    onLanded: onTurnRecovered,
    onLost: onTurnLost,
  });

  return {
    isReconnecting: machine.isReconnecting,
    isLost: machine.isLost,
    onTurnSettled: machine.onTurnSettled,
    onFailure: machine.onFailure,
    onTurnSent: (question) => {
      machine.begin(question.trim() === "" ? null : { question });
    },
  };
}

/**
 * The conversation a pending FIRST turn belongs to, out of the newest page of the
 * history list — or null while it is not there yet. Exported for its own test: it
 * is the whole identity argument of the new-chat and shared-fork recoveries.
 *
 * `conversationId` known (the stream reached `data-conversation`, or a file was
 * attached before asking) → identity is the id, and it appears in the list only
 * once its first turn is persisted (empty transcripts are filtered out server
 * side).
 *
 * Unknown → the newest conversation whose LAST turn is the question we sent. Its
 * last and not its first: a shared-link fork persists as `sharedTranscript + turn`,
 * so its first message belongs to the discussion being resumed and matching on it
 * never fired at all. (Worst case — the turn never landed AND the user's newest
 * conversation happens to end on the identical question — sends them to that
 * conversation, i.e. to an answer to what they asked, instead of to an error.)
 */
export function matchPendingConversation(
  docs: ReadonlyArray<{
    id: string;
    messages: Parameters<typeof lastTurnOf>[0];
  }>,
  conversationId: string | null,
  question: string
): string | null {
  if (conversationId !== null)
    return docs.some((doc) => doc.id === conversationId)
      ? conversationId
      : null;
  const newest = docs.at(0);
  if (newest === undefined) return null;
  const turn = lastTurnOf(newest.messages);
  if (turn === null) return null;
  return turn.question.trim() === question.trim() ? newest.id : null;
}
